import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import cx from 'classnames';
import styles from './Onboarding.module.scss';
import { Helmet } from 'react-helmet';
import { update as updateUser } from 'redux/modules/users';
import { getSubjects, getLevels } from 'redux/modules/topics';

const OnboardingFormComponent = ({
  onBackFirst,
  stage,
  setStage,
  setFirstDone,
  onSubmit,
  subjects,
  levels,
}) => {
  const [filter, setFilter] = useState('');
  const [selectedSubjects, setSubjects] = useState([]);
  const levelsList = useMemo(
    () =>
      levels.map(s => ({ item: s, display: s.toLowerCase().includes(filter) })),
    [filter, levels]
  );
  const handleSearch = useCallback(
    ({ target }) => setFilter(target.value.toLowerCase()),
    []
  );
  const handleSubmit = e => {
    e.preventDefault();
    const items = [...e.target.elements]
      .filter(item => item.checked)
      .map(item => item.id);
    setFirstDone(true);
    if (stage === 1) {
      setSubjects(items);
      setStage(stage + 1);
    } else if (stage === 2) {
      setStage(stage + 1);
      onSubmit({ subscribedTopics: [...selectedSubjects, ...items] });
    }
  };
  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className={cx(stage !== 1 && styles['d-none'])}
      >
        <div className={styles.subBar}>
          <span>CHOOSE SUBJECTS YOU'RE INTERESTED IN</span>
          <button className={styles.nextButton} type="submit">
            NEXT
          </button>
        </div>
        <div className={styles.gridContainer}>
          {subjects.map(({ value, name }) => (
            <label htmlFor={value} key={value}>
              <div className={styles.imageBox}>
                <span className={styles.boxText}>{name}</span>
                <div className={styles.round}>
                  <input type="checkbox" id={value} defaultChecked={false} />
                  <label htmlFor={value}></label>
                </div>
              </div>
            </label>
          ))}
        </div>
      </form>
      <form
        onSubmit={handleSubmit}
        className={cx(stage !== 2 && styles['d-none'])}
      >
        <div className={styles.subBar}>
          <span>CHOOSE YOUR LEVEL & INTEREST</span>
          <div style={{ display: 'flex' }}>
            <button
              className={styles.backButton}
              type="button"
              onClick={onBackFirst}
            >
              BACK
            </button>
            <button className={styles.nextButton} type="submit">
              FINISH
            </button>
          </div>
        </div>
        <div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search..."
            onChange={handleSearch}
          />
          <div className={styles.levelsGrid}>
            {levelsList.map(({ item, display }) => (
              <div key={item} className={display ? '' : styles['d-none']}>
                <div className={styles.round}>
                  <input type="checkbox" id={item} defaultChecked={false} />
                  <label htmlFor={item}></label>
                </div>
                <label className={styles.clickable} htmlFor={item}>
                  {item}
                </label>
              </div>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
};

OnboardingFormComponent.propTypes = {
  subjects: PropTypes.array.isRequired,
  levels: PropTypes.array.isRequired,
  onBackFirst: PropTypes.func.isRequired,
  stage: PropTypes.number.isRequired,
  setStage: PropTypes.func.isRequired,
  setFirstDone: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const OnboardingForm = connect(state => ({
  subjects: getSubjects(state),
  levels: getLevels(state),
}))(OnboardingFormComponent);

const Onboarding = ({ push, updateUser, userId, isAuth }) => {
  useEffect(() => {
    if (!isAuth) push('/forum');
  }, [isAuth, push]);
  const [stage, setStage] = useState(1);
  const [firstDone, setFirstDone] = useState(false);
  const handleBackFirst = () => stage === 2 && setStage(1);
  const handleSubmit = useCallback(
    data => updateUser({ _id: userId, ...data }).then(() => push('/forum')),
    [push, updateUser, userId]
  );
  return (
    <div className={cx(styles.mainContainer, 'container')}>
      <Helmet>
        <title>Onboarding - Forums</title>
      </Helmet>
      <div className={styles.navRow}>
        <div className={stage === 1 ? styles.selector : styles.finished}>
          <span>1</span>
        </div>
        <div className={cx(styles.dash, firstDone && styles.highlight)}></div>
        <div
          className={
            !firstDone
              ? styles.lock
              : stage === 2
              ? styles.selector
              : styles.finished
          }
        >
          <span>2</span>
        </div>
      </div>
      <div className="col-lg-9 col-md-9 col-sm-12 col-xs-12">
        <div className={styles.headingRow}>
          <span className={styles.headingText}>
            {stage === 3
              ? 'Personalizing your feed.'
              : 'Welcome to the SmartGuppy Forum!'}
          </span>
          <p>
            {'This ' +
              (stage === 3
                ? 'will take a minute'
                : 'is the place where you can reach out to our vibrant community and ask questions.')}
          </p>
        </div>
        {stage !== 3 && (
          <OnboardingForm
            onBackFirst={handleBackFirst}
            stage={stage}
            setStage={setStage}
            setFirstDone={setFirstDone}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
};

Onboarding.propTypes = {
  userId: PropTypes.string,
  isAuth: PropTypes.bool.isRequired,
  push: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
};

export default connect(
  ({ auth: { user } }) => ({ userId: user, isAuth: !!user }),
  { push, updateUser }
)(Onboarding);
